import { useCallback, useEffect, useRef, useState } from "react";

import { useSearchParams } from "next/navigation";
import { useSnapshot } from "valtio";
import { rootStore, useSessionStorage } from "../store";

import { sessionMap } from "../store/sessionMap";
import { encodeString } from "../utils/stringEncoder";
import { useLoginModalProvider } from "./useLoginModalProvider";
import { useRedirectChat } from "./useRedirectChat";
import { usePathname } from "@hooks/usePathname";
import type { SenderSubmitOptions } from "@/src/components/Sender/type";

export const useHomePageCommonLogic = () => {
  const {
    isLoggedIn,
    isLoading: loginLoading,
    isFetching: isLoginFetching,
  } = useSnapshot(rootStore.login);
  const { ownerInfo } = useSnapshot(rootStore.user);
  const searchParams = useSearchParams();
  const pathname = usePathname();
  const key = searchParams.get("key") || "";
  const question = searchParams.get("question");

  const [isStartLoading, setIsStartLoading] = useState(!!key);
  const [hasError, setHasError] = useState(false);

  // 创建聊天时的错误回调
  const handleCreateChatError = useCallback(() => {
    setIsStartLoading(false);
    setHasError(true);
  }, []);

  // URL 重定向和用户输入使用独立的实例，避免防重复机制冲突
  const { handleCheckChatVaild: handleUrlRedirect, loading: urlLoading } =
    useRedirectChat({ createChatError: handleCreateChatError });

  const { handleCheckChatVaild: handleUserSend, loading: userSendLoading } =
    useRedirectChat({
      createChatError: handleCreateChatError,
    });

  // 推荐登录相关
  const store = useSessionStorage(sessionMap.REFERRAL_AUTO_LOGIN, false);
  const { open } = useLoginModalProvider();
  const isAutoLogin = store.getValue();
  const hasReferral =
    pathname.startsWith("/referral/") || pathname.includes("referral");

  // 同步 URL 重定向的 loading 状态
  useEffect(() => {
    setIsStartLoading(urlLoading);
  }, [urlLoading]);

  // 处理外部跳转：只在首次加载且有 question 时执行
  const hasProcessedInitialQuestion = useRef(false);

  useEffect(() => {
    // 只处理一次初始的 URL 参数
    if (
      hasProcessedInitialQuestion.current ||
      (!key && !question) ||
      loginLoading ||
      isLoginFetching ||
      hasError
    ) {
      return;
    }

    hasProcessedInitialQuestion.current = true;

    const [val = "", ...signRest] = key.split("_") || [];
    const sign = question ? key : signRest?.join("_");
    handleUrlRedirect({
      question: question ? question : val,
      sign,
      setIsStartLoading,
      routerType: "replace",
    });
  }, [question, loginLoading, isLoginFetching, hasError, handleUrlRedirect]);

  // 用户手动发送消息
  const onSend = useCallback(
    async (
      inputValue = "",
      recommendOrSubmit?:
        | {
            source_id?: string;
            question?: string;
          }
        | SenderSubmitOptions,
      submitOptions?: SenderSubmitOptions,
    ) => {
      setHasError(false);
      const isSenderSubmitOptions =
        !!recommendOrSubmit &&
        typeof recommendOrSubmit === "object" &&
        ("attachments" in recommendOrSubmit ||
          "forceAttachments" in recommendOrSubmit ||
          "voiceContext" in recommendOrSubmit);
      const recommendQuestionInfo = isSenderSubmitOptions
        ? undefined
        : (recommendOrSubmit as
            | {
                source_id?: string;
                question?: string;
              }
            | undefined);

      const senderSubmitOptions = isSenderSubmitOptions
        ? (recommendOrSubmit as SenderSubmitOptions)
        : submitOptions;
      const customParams = {
        attachments: senderSubmitOptions?.attachments,
        voiceContext: senderSubmitOptions?.voiceContext,
      };

      // 如果提供了推荐问题信息，构建 sign 和 cacheQuestion 参数
      if (recommendQuestionInfo) {
        const { source_id, question } = recommendQuestionInfo;
        const sign = `content_${encodeString(`chat:id#${source_id}`)}`;

        await handleUserSend({
          question: inputValue,
          sign,
          cacheQuestion: question,
          senderContext: true,
          customParams,
        });
      } else {
        await handleUserSend({
          question: inputValue,
          senderContext: true,
          customParams,
        });
      }
    },
    [handleUserSend],
  );

  // 推荐登录弹窗：只在需要时显示一次
  const hasShownLoginModal = useRef(false);

  useEffect(() => {
    const shouldShow =
      hasReferral && !isAutoLogin && !isLoggedIn && !ownerInfo?.id;
    if (shouldShow && !hasShownLoginModal.current) {
      hasShownLoginModal.current = true;
      open?.();
    }
  }, [isLoggedIn, ownerInfo?.id, hasReferral, isAutoLogin, open]);

  return {
    isStartLoading,
    sendLoading: urlLoading || userSendLoading,
    onSend,
  };
};
